import { DepartmentDetailResponse, WorkTemplateDetail } from 'api';
import { ShiftPayloadBase } from 'api/services/shift/shift-service.dto';
import { invariant } from 'shared/utils/utils';
import { SkillType } from '@types';
import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { WorkFormContext } from '../work-form.types';
import { eurosToCents } from '../../../utils/formatting';

export function mapWorkTemplateToForm(
  workTemplate: WorkTemplateDetail | undefined,
  departmentData: DepartmentDetailResponse | undefined
): Partial<WorkFormContext> | undefined {
  if (!workTemplate) {
    return undefined;
  }

  return {
    workName: workTemplate.name,
    jobId: workTemplate.id,
    companyOption: undefined,
    ...(workTemplate.departmentId
      ? {
          department: workTemplate.departmentId,
        }
      : {}),
    breakMinutes: workTemplate.breakMinutes.toString(),
    isFreelance: workTemplate.freelanceJob != null,
    hourlyRate:
      workTemplate.freelanceJob?.hourlyRateCents != null
        ? (workTemplate.freelanceJob?.hourlyRateCents / 100).toString()
        : undefined,
    mutableUntilHours:
      workTemplate.freelanceJob?.mutableUntilMinutes != null
        ? (workTemplate.freelanceJob?.mutableUntilMinutes / 60).toString()
        : undefined,
    isTempwork: workTemplate.tempWorkJob != null,
    cla: workTemplate.tempWorkJob?.cla,
    isEmployee: workTemplate.isEmployee,
    jobType: workTemplate.jobTypeId ?? undefined,
    contactName: workTemplate.contactName,
    contactPhoneWithPrefix: workTemplate.contactPhoneNumber,
    flexPool: undefined,
    flexPools: workTemplate.flexPools,
    isFlexPoolOnly: workTemplate.flexPoolOnly,
    imageUrl: workTemplate.imageUrl ?? undefined,
    shortDescription: workTemplate.descriptionShort,
    longDescription: workTemplate.descriptionLong,
    descriptionLegalRequirements: workTemplate.descriptionLawRequirements,
    vat: workTemplate.VAT,
    location: resolveLocation(workTemplate, departmentData),
    jobCertificates: workTemplate.jobCertificates,
    requiredCertificates: workTemplate.requiredCertificates,
    isOrtApplicable: workTemplate.ortApplicable,
    costCenter: workTemplate.costCenter ?? undefined,
    jobRequirements: workTemplate.jobRequirements,
  };
}

export function mapShiftDetailToForm(shift: ShiftDetail | undefined): WorkFormContext | undefined {
  if (!shift) {
    return undefined;
  }

  return {
    jobId: undefined,
    workName: shift.name,
    companyOption: {
      label: shift.companyName,
      value: shift.companyId,
    },
    department: shift.departmentId,
    startDate: shift.startDate,
    endDate: shift.endDate,
    startTime: shift.startTime,
    endTime: shift.endTime,
    maxClaimants: shift.maxClaimants.toString(),
    breakMinutes: shift.breakMinutes.toString(),
    isFreelance: shift.freelanceProperties != null,
    hourlyRate:
      shift.freelanceProperties?.hourlyRateCents != null
        ? (shift.freelanceProperties?.hourlyRateCents / 100).toString()
        : undefined,
    mutableUntilHours:
      shift.freelanceProperties?.mutableUntilMinutes != null
        ? (shift.freelanceProperties?.mutableUntilMinutes / 60).toString()
        : undefined,
    isTempwork: shift.tempWorkProperties != null,
    cla: shift.tempWorkProperties?.cla,
    isEmployee: shift.isEmployee,
    jobType: shift.jobTypeId ?? undefined,
    contactName: shift.contactName,
    contactPhoneWithPrefix: shift.contactPhoneNumber,
    flexPool: undefined,
    flexPools: shift.flexPools,
    isFlexPoolOnly: shift.flexPoolOnly,
    imageUrl: shift.imageUrl ?? undefined,
    shortDescription: shift.descriptionShort,
    longDescription: shift.descriptionLong,
    descriptionLegalRequirements: shift.descriptionLawRequirements,
    vat: shift.vat,

    location: shift.location,
    jobCertificates: shift.shiftCertificates,
    requiredCertificates: shift.jobTypeCertificates,
    isOrtApplicable: shift.isOrtApplicable,
    costCenter: shift.costCenter ?? undefined,
    jobRequirements: {
      skills: shift.requirements?.filter((item) => item.type === SkillType.SOFT_SKILL),
      language: shift.requirements?.filter((item) => item.type === SkillType.LANGUAGE),
      apparel: shift.requirements?.filter((item) => item.type === SkillType.APPAREL),
    },
  };
}

export function mapFormDataToDto(
  formData: WorkFormContext,
  defaultValues: Partial<WorkFormContext> | undefined
): ShiftPayloadBase {
  const {
    companyOption,
    department,
    startDate,
    endDate,
    startTime,
    endTime,
    breakMinutes,
    maxClaimants,
    isFreelance,
    hourlyRate,
    mutableUntilHours,
    isTempwork,
    cla,
    isEmployee,
    jobType,
    location,
    contactName,
    contactPhoneWithPrefix,
    vat,
    flexPools,
    isFlexPoolOnly,
    imageUrl,
    shortDescription,
    longDescription,
    descriptionLegalRequirements,
    jobCertificates = [],
    requiredCertificates = [],
  } = formData;

  invariant(defaultValues, 'defaultValues is required');
  const { workName, isOrtApplicable, costCenter, jobRequirements } = defaultValues;

  invariant(workName, 'workName is required');
  invariant(companyOption?.value, 'companyId is required');
  invariant(department, 'department is required');
  invariant(startDate, 'startDate is required');
  invariant(endDate, 'endDate is required');
  invariant(startTime, 'startTime is required');
  invariant(endTime, 'endTime is required');
  invariant(maxClaimants, 'maxClaimants is required');
  invariant(breakMinutes, 'breakMinutes is required');
  invariant(jobType, 'jobType is required');
  invariant(location, 'location is required');
  invariant(contactName, 'contactName is required');
  invariant(contactPhoneWithPrefix, 'contactPhoneWithPrefix is required');
  invariant(vat, 'vat is required');
  invariant(flexPools, 'flexPools is required');
  invariant(isFlexPoolOnly, 'isFlexPoolOnly is required');
  invariant(shortDescription, 'shortDescription is required');
  invariant(longDescription, 'longDescription is required');
  invariant(descriptionLegalRequirements, 'descriptionLegalRequirements is required');
  invariant(isOrtApplicable, 'isOrtApplicable is required');

  if (isFreelance && (hourlyRate == null || mutableUntilHours == null)) {
    throw new Error('hourlyRate and mutableUntilHours are required for freelance jobs');
  }
  if (isTempwork && cla == null) {
    throw new Error('cla is required for temp work jobs');
  }

  return {
    name: workName,
    departmentId: department,
    startDate: `${formData.startDate}T${formData.startTime}`,
    endDate: `${formData.endDate}T${formData.endTime}`,
    maxClaimants: Number(maxClaimants),
    breakMinutes: Number(breakMinutes),
    freelanceProperties:
      isFreelance && hourlyRate && mutableUntilHours
        ? {
            hourlyRateCents: eurosToCents(hourlyRate),
            mutableUntilMinutes: Number(mutableUntilHours) * 60,
          }
        : undefined,
    tempWorkProperties:
      isTempwork && cla
        ? {
            cla,
          }
        : undefined,
    isEmployee: !!isEmployee,
    jobTypeId: jobType,
    location,
    contactName,
    contactPhoneNumber: contactPhoneWithPrefix,
    VAT: vat,
    flexPools,
    flexPoolOnly: isFlexPoolOnly,
    imageUrl,
    descriptionShort: shortDescription,
    descriptionLong: longDescription,
    descriptionLawRequirements: descriptionLegalRequirements,
    shiftCertificateIds: [...jobCertificates, ...requiredCertificates].map(
      (certificate) => certificate.id
    ),
    ortApplicable: isOrtApplicable,
    costCenter,
    jobRequirementIds: [
      ...(jobRequirements?.skills ?? []),
      ...(jobRequirements?.language ?? []),
      ...(jobRequirements?.apparel ?? []),
    ].map((item) => item.id),
  };
}

const resolveLocation = (
  workTemplate: WorkTemplateDetail,
  departmentData: DepartmentDetailResponse | undefined
) => {
  if (workTemplate.location) {
    return workTemplate.location;
  }
  if (departmentData?.department.location) {
    return departmentData.department.location;
  }

  return;
};
